import { useEffect, useMemo, useRef, useState } from "react";
import {
  CarouselWrap,
  Slide,
  Image,
  Overlay,
  Caption,
  Title,
  Subtitle,
  NavBtn,
  Dots,
  Dot,
  ButtonRow,
  CTAButton,
} from "./HeroCarouselStyles";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { useTranslation } from "react-i18next";

export default function HeroCarousel({
  slides,
  interval = 4200,
  height = "70vh",
  accent = "#a78631",
  onMenuClick = () => window.open("/meniu_delivery/", "_self"),
  onContactClick = () => window.open("tel:+40268456156", "_self"),
}) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);
  const wrapRef = useRef(null);
  const len = useMemo(() => slides.length, [slides]);

  useEffect(() => {
    start();
    return stop;
  }, [index, interval, len]);

  const start = () => {
    stop();
    timerRef.current = setTimeout(
      () => setIndex((i) => (i + 1) % len),
      interval
    );
  };
  const stop = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    let startX = 0,
      isDown = false;

    const onStart = (e) => {
      isDown = true;
      startX = e.touches ? e.touches[0].clientX : e.clientX;
      stop();
    };
    const onMove = (e) => {
      if (!isDown) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      const dx = x - startX;
      if (Math.abs(dx) > 60) {
        isDown = false;
        dx > 0 ? prev() : next();
      }
    };
    const onEnd = () => {
      isDown = false;
      start();
    };

    el.addEventListener("mousedown", onStart);
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseup", onEnd);
    el.addEventListener("mouseleave", onEnd);
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: true });
    el.addEventListener("touchend", onEnd);

    return () => {
      el.removeEventListener("mousedown", onStart);
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseup", onEnd);
      el.removeEventListener("mouseleave", onEnd);
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
    };
  }, [len]);

  const prev = () => setIndex((i) => (i - 1 + len) % len);
  const next = () => setIndex((i) => (i + 1) % len);

  return (
    <CarouselWrap
      style={{ "--hero-h": height, "--accent": accent }}
      onMouseEnter={stop}
      onMouseLeave={start}
      ref={wrapRef}
      aria-roledescription="carousel"
    >
      {slides.map((s, i) => (
        <Slide
          key={s.key || i}
          $active={i === index}
          role="group"
          aria-label={`${i + 1} / ${len}`}
        >
          <Image src={s.src} alt={s.alt || ""} loading="eager" />
          <Overlay />
          <Caption>
            {s.title && <Title>{s.title}</Title>}
            {s.text && <Subtitle>{s.text}</Subtitle>}
            <ButtonRow>
              <CTAButton data-variant="ghost" onClick={onMenuClick}>
                {t("hero.ctaMenu")}
              </CTAButton>
              <CTAButton data-variant="ghost" onClick={onContactClick}>
                {t("hero.ctaContact")}
              </CTAButton>
            </ButtonRow>
          </Caption>
        </Slide>
      ))}
      <NavBtn aria-label="Anterior" onClick={prev} left>
        <FiChevronLeft size={20} />
      </NavBtn>
      <NavBtn aria-label="Următor" onClick={next} right>
        <FiChevronRight size={20} />
      </NavBtn>
      <Dots role="tablist" aria-label="Indicatori">
        {slides.map((_, i) => (
          <Dot
            key={i}
            aria-label={`Slide ${i + 1}`}
            aria-current={i === index}
            $active={i === index}
            onClick={() => setIndex(i)}
          />
        ))}
      </Dots>
    </CarouselWrap>
  );
}
